import { useEffect, useMemo, useState } from 'react';

import {
  AutoComplete,
  ChakraBox,
  ChakraFlex,
  ChakraText,
  Close,
  EquipUIFireflyV2Theme,
  IconButton,
  Search,
  User,
  UserAvatarCard,
} from '@equip.health/ui';
import { useHistory } from 'react-router-dom';

import { ApiCommand } from '~/lib/Api';
import { PATIENT_SEARCH_PAGE_SIZE } from '~/lib/constants/patients.constants';
import urlConstants from '~/lib/constants/url.constants';
import {
  UNASSIGNED_OPTION,
  UNASSIGNED_USER,
} from '~/lib/constants/user.constants';
import { useGlobalContext } from '~/lib/contexts/GlobalContext';
import { useApi } from '~/lib/hooks';
import { getPreferredFullName } from '~/lib/util/patient.util';
import {
  getSecondaryUserOptionText,
  getUserOption,
} from '~/lib/util/user.util';
import { Path } from '~/routes/constants';

const { neutral } = EquipUIFireflyV2Theme.colors;

const DEFAULT_TOTAL_COUNT = 1;

type PatientSelectionProps = {
  autoFillPatient?: boolean;
  hideLabel?: boolean;
  id: string;
  isDisabled?: boolean;
  patient: Patient | undefined | null;
  showProfileIcon?: boolean;
  updatePatient: (newPatient: Patient) => void;
};

const PatientSelection = ({
  autoFillPatient = false,
  hideLabel = false,
  id,
  isDisabled = false,
  patient,
  showProfileIcon = false,
  updatePatient,
}: PatientSelectionProps) => {
  const TOP_LEVEL_ID = `${id}-patient`;

  const [pageNumber, setPageNumber] = useState<number>(1);
  const [totalCount, setTotalCount] = useState<number>(DEFAULT_TOTAL_COUNT);
  // TODO: best practice says we should use a reducer anytime a state variable gets even slightly
  // more complex than a simple primitive type
  const [patientList, setPatientList] = useState<Patient[]>([]);

  const { loading: isGetPatientsLoading, sendRequest: getPatients } =
    useApi<GetPatientsResponse>();

  const updatePatientList =
    (newPageNumber: number) =>
    (results: GetPatientsResponse): void => {
      const { count, data } = results;
      if (data) {
        setPatientList((curr: Patient[]): Patient[] => {
          const updatedPatientsList =
            newPageNumber === 1 ? (data ?? []) : [...curr, ...(data ?? [])];

          setTotalCount(count + DEFAULT_TOTAL_COUNT);
          setPageNumber(newPageNumber);

          return updatedPatientsList;
        });
      }
    };

  const handleSearch = (searchTerm: string, isFirstPage = true): void => {
    const newPageNumber = isFirstPage ? 1 : pageNumber + 1;
    const options = {
      'page-number': newPageNumber,
      'page-size': PATIENT_SEARCH_PAGE_SIZE,
      'sort-by': 'name',
    };
    if (searchTerm) {
      options['filter-by'] = {
        'patient-search': searchTerm.trim().split(' ').join('+'),
      };
    }

    getPatients({
      callback: updatePatientList(newPageNumber),
      command: ApiCommand.GET,
      options,
      url: urlConstants.users.getPatients,
    });
  };

  const history = useHistory();
  const { currentPatientId } = useGlobalContext();

  const patientOptions: AutoCompleteOption[] = useMemo(
    () => [UNASSIGNED_OPTION, ...(patientList ?? []).map(getUserOption())],
    [patientList],
  );

  const { sendRequest: getPatientById } = useApi<Patient>();

  useEffect(() => {
    // In read-only mode (isDisabled == true), do not overwrite the patient (PT-1366)
    if (autoFillPatient && currentPatientId && !isDisabled) {
      getPatientById({
        callback: updatePatient,
        command: ApiCommand.GET,
        url: urlConstants.patients.getPatientById(currentPatientId),
      });
    }
  }, []);

  const handlePatientSelect = (option: AutoCompleteOption): void => {
    updatePatient(
      option?.value === UNASSIGNED_OPTION.value
        ? UNASSIGNED_USER
        : (patientList ?? []).find(
            ({ userId: patientId }: Patient) => patientId === option?.value,
          ),
    );
  };

  const { avatarSrc, chosenName, firstName, lastName, userId } = patient ?? {};

  return patient ? (
    <>
      {!hideLabel && (
        <ChakraText
          color="neutral.secondary"
          fontSize="12px"
          fontWeight="400"
          lineHeight="21.6px"
          marginBottom="8px"
          marginLeft="14px"
        >
          Patient
        </ChakraText>
      )}
      <ChakraFlex
        alignItems="center"
        background="neutral.background.secondary"
        borderRadius="16px"
        justifyContent="space-between"
        marginTop="0px !important"
        padding="24px"
      >
        <UserAvatarCard
          avatarSize="large"
          avatarSrc={avatarSrc}
          id={TOP_LEVEL_ID}
          primaryText={[getPreferredFullName(chosenName, firstName, lastName)]}
          primaryTextBold
          secondaryText={getSecondaryUserOptionText(patient)}
          showGenericIcon={userId === UNASSIGNED_USER.userId}
        />
        {!isDisabled && (
          <ChakraFlex>
            {showProfileIcon && (
              <IconButton
                ariaLabel="view patient profile"
                icon={<User fill={neutral.secondary} />}
                id={`${TOP_LEVEL_ID}-profile`}
                onClick={() => history.push(`${Path.MAUD_PATIENTS}/${userId}`)}
                size="sm"
                variant="ghost"
              />
            )}
            <IconButton
              ariaLabel="clear patient"
              icon={<Close fill={neutral.secondary} />}
              id={`${TOP_LEVEL_ID}-clear`}
              onClick={() => updatePatient(undefined)}
              size="sm"
              variant="ghost"
            />
          </ChakraFlex>
        )}
      </ChakraFlex>
    </>
  ) : (
    <AutoComplete
      endIcon={
        <ChakraBox
          background="primary.100"
          borderRadius="8px"
          boxShadow="0px 4px 32px rgba(0, 127, 121, 0.4)"
          height="30px"
          paddingLeft="7px"
          paddingTop="7px"
          width="30px"
        >
          <Search fillOpacity={1} height="20px" width="20px" />
        </ChakraBox>
      }
      fetchOptions={handleSearch}
      id={TOP_LEVEL_ID}
      isDebounced
      isInfiniteScrollable
      isLoading={isGetPatientsLoading}
      onSelect={handlePatientSelect}
      options={patientOptions}
      placeholder="Link to a patient"
      selected={[]}
      shouldFetchOnListOpen
      title={hideLabel ? undefined : 'Patient'}
      toggleListOnFocus
      totalOptionsCount={totalCount}
      useGenericIconWhen={(option: AutoCompleteOption) =>
        option.value === UNASSIGNED_OPTION.value
      }
    />
  );
};

export default PatientSelection;
